import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { dynamicArtifactTemplateSchema } from '@eluve/llm-outputs';

const c = initContract();

// Duplicated schema from medical document schema generator workflow
// to avoid importing backend dependencies in shared contract
const medicalDocumentSchemaOutputSchema = z.object({
  jsonSchema: z
    .object({
      $schema: z.literal('http://json-schema.org/draft-07/schema#'),
      type: z.literal('object'),
      description: z.string().describe('Overall document intent and purpose'),
      properties: z
        .record(z.string(), z.any())
        .describe(
          'All of the properties that must be included in the document adhereing to valid JSON schema format',
        ),
    })
    .describe(
      'Complete JSON schema object that validates the requested medical document structure',
    ),
  explanation: z
    .string()
    .describe('Explanation of the schema structure and design decisions'),
});

const createOutputTemplateResponseSchema =
  medicalDocumentSchemaOutputSchema.extend({
    dynamicArtifact: dynamicArtifactTemplateSchema.describe(
      'Dynamic artifact template generated from the JSON schema for UI form rendering',
    ),
    hydrateJson: z
      .object({
        type: z
          .literal('hydrateArtifact')
          .describe('Type identifier for hydrate artifact'),
        artifactTemplate: dynamicArtifactTemplateSchema.describe(
          'The dynamic artifact template wrapped for hydration',
        ),
        outputType: z.literal('SOAP_NOTE'),
        isImported: z.boolean().describe('Whether this artifact was imported'),
      })
      .describe('Wrapped dynamic artifact for hydration purposes'),
  });

export const adminLlmContract = c.router(
  {
    summarizeWithOutputTemplate: {
      method: 'POST',
      path: '/summarize-with-output-template',
      body: z
        .object({
          promptTemplateId: z.string(),
          outputTemplate: dynamicArtifactTemplateSchema,
          outputLanguage: z.string(),
          transcript: z.string().optional(),
          appointmentId: z.string().optional(),
        })
        .refine((data) =>
          data.transcript || data.appointmentId
            ? true
            : 'Either transcript or appointmentId must be provided',
        ),
      responses: {
        201: z.object({
          success: z.boolean(),
          output: z.unknown().optional(),
        }),
        422: z.object({
          message: z.string(),
        }),
      },
    },
    createOutputTemplate: {
      method: 'POST',
      path: '/create-output-template',
      body: z.object({
        documentDescription: z
          .string()
          .describe(
            'Description of the desired medical document format, including sections, fields, and structure requirements',
          )
          .min(1, 'Document description is required'),
        documentType: z
          .string()
          .describe(
            'Type of medical document (e.g., "clinical note", "lab report", "discharge summary", "patient assessment")',
          )
          .optional(),
        requiredFields: z
          .array(z.string())
          .describe(
            'List of required fields that must be included in the schema',
          )
          .optional(),
        constraints: z
          .string()
          .describe(
            'Any specific constraints, validation rules, or formatting requirements',
          )
          .optional(),
        presignedUrl: z
          .string()
          .optional()
          .describe('Presigned URL for file upload'),
      }),
      responses: {
        201: createOutputTemplateResponseSchema,
        422: z.object({
          message: z.string(),
        }),
      },
    },
  },
  { pathPrefix: '/llm' },
);
